/**
 * Preview postMessage contract — the single source of truth for the channel
 * between the admin dashboard's iframe panel (apps/admin) and the chrome-less
 * `/preview/:slug` route in this app (apps/web).
 *
 * FLOW
 *   1. PreviewPage mounts inside the admin's <iframe> and posts
 *      `corgi-preview-ready` to window.parent.
 *   2. The admin replies — and thereafter streams on every form edit — with
 *      `corgi-preview` carrying the full draft DDB Fund (its unsaved form state
 *      with `id` = slug; ideally the output of the admin's shared buildFundInput
 *      so the preview matches exactly what Save will persist).
 *   3. PreviewPage renders the REAL fund-detail template from that draft.
 *
 * SECURITY — the admin and web apps are DIFFERENT origins (separate CloudFront
 * distributions), so this is a CROSS-ORIGIN channel:
 *   • The preview MUST validate `event.origin` with {@link isAllowedAdminOrigin}
 *     before trusting any inbound message.
 *   • The admin MUST send the draft to the web origin explicitly (never "*").
 */
import type { Fund as DdbFund } from "@etfsite/db/types";

// ── Message types ──────────────────────────────────────────────────────────────

export const PREVIEW_DRAFT_TYPE = "corgi-preview" as const;
export const PREVIEW_READY_TYPE = "corgi-preview-ready" as const;
export const PREVIEW_SCROLL_TYPE = "corgi-preview-scroll" as const;

/** Inbound (admin → preview): the draft fund config to render. */
export interface PreviewDraftMessage {
  type: typeof PREVIEW_DRAFT_TYPE;
  /** A full DDB Fund record — the admin's draft form state plus `id` = slug. */
  draft: DdbFund;
  /**
   * Leaf paths that differ from the saved baseline (e.g. "summary",
   * "expenseRatio", "bufferConfig.bufferLevel") — drives the change-highlight
   * boxes. Optional/absent on legacy admin builds that predate the feature.
   */
  changedPaths?: string[];
  /** Whether the preview should render the change-highlight boxes. */
  highlight?: boolean;
}

/** Outbound (preview → admin): the iframe has mounted and is listening. */
export interface PreviewReadyMessage {
  type: typeof PREVIEW_READY_TYPE;
}

/**
 * Bidirectional (preview ↔ admin): the user scrolled this preview. The admin
 * relays it to the OTHER preview iframe so the side-by-side Compare overlay
 * scrolls in lock-step. Carried as a 0..1 ratio of scrollable height — robust to
 * the baseline and draft renders having different content heights.
 */
export interface PreviewScrollMessage {
  type: typeof PREVIEW_SCROLL_TYPE;
  ratio: number;
}

export type PreviewMessage = PreviewDraftMessage | PreviewReadyMessage | PreviewScrollMessage;

/**
 * Shape-narrowing guard for an inbound draft message. Validates the envelope
 * only (type + a non-null object draft); it does NOT validate the origin —
 * callers MUST check {@link isAllowedAdminOrigin} on `event.origin` first.
 */
export function isPreviewDraftMessage(data: unknown): data is PreviewDraftMessage {
  if (typeof data !== "object" || data === null) return false;
  const m = data as { type?: unknown; draft?: unknown };
  return m.type === PREVIEW_DRAFT_TYPE && typeof m.draft === "object" && m.draft !== null;
}

/** Shape-narrowing guard for a scroll-sync message (a finite numeric ratio). */
export function isPreviewScrollMessage(data: unknown): data is PreviewScrollMessage {
  if (typeof data !== "object" || data === null) return false;
  const m = data as { type?: unknown; ratio?: unknown };
  return m.type === PREVIEW_SCROLL_TYPE && typeof m.ratio === "number" && Number.isFinite(m.ratio);
}

// ── Change-highlight path matching ───────────────────────────────────────────

/**
 * Does a `data-fund-field` / `data-fund-section` marker correspond to one of the
 * changed leaf paths?
 *
 * The marker may carry a SPACE-SEPARATED list of paths (a section that covers
 * several fields, e.g. `data-fund-section="sectors sectorLabel founders"`). A
 * token matches a changed path when either is an ancestor-or-equal of the other:
 *   • exact:        "expenseRatio"          ↔ "expenseRatio"
 *   • marker ⊃ path: section "documents"    ↔ leaf "documents.prospectusUrl"
 *   • path ⊃ marker: whole "bufferConfig"   ↔ field "bufferConfig.bufferLevel"
 * The second case lets a coarse section box a nested change; the third lets a
 * fine field still light up when the whole parent object was swapped (null→{…}).
 */
export function markerMatchesChanges(marker: string, changedPaths: readonly string[]): boolean {
  const tokens = marker.split(/\s+/).filter(Boolean);
  return tokens.some((token) =>
    changedPaths.some(
      (path) => token === path || path.startsWith(`${token}.`) || token.startsWith(`${path}.`),
    ),
  );
}

// ── Trusted admin origins ───────────────────────────────────────────────────────

/**
 * Admin origins permitted to drive the preview. Mirrors the admin app's
 * ADMIN_URLS (per-env CloudFront) + custom admin domains, plus the local admin
 * dev server. An optional `VITE_ADMIN_ORIGIN` (build-time) extends the set
 * without a code edit — handy if an admin domain is added later.
 */
const STATIC_ADMIN_ORIGINS: readonly string[] = [
  // Per-env admin CloudFront distributions (mirror apps/admin ADMIN_URLS).
  "https://d4vpghwbh845g.cloudfront.net", // development
  "https://ds57oatx72pvx.cloudfront.net", // staging
  "https://dquz9dwwwcto2.cloudfront.net", // production
  // Custom admin domains.
  "https://admin.corgifunds.com",
  "https://development.admin.corgifunds.com",
  "https://staging.admin.corgifunds.com",
  // Local admin vite dev server (default :5174, fallback :5175).
  "http://localhost:5174",
  "http://localhost:5175",
];

const EXTRA_ADMIN_ORIGIN = (import.meta.env.VITE_ADMIN_ORIGIN as string | undefined)?.trim();

export const ALLOWED_ADMIN_ORIGINS: ReadonlySet<string> = new Set(
  EXTRA_ADMIN_ORIGIN ? [...STATIC_ADMIN_ORIGINS, EXTRA_ADMIN_ORIGIN] : STATIC_ADMIN_ORIGINS,
);

/** True iff `origin` is one of our trusted admin surfaces. */
export function isAllowedAdminOrigin(origin: string): boolean {
  return ALLOWED_ADMIN_ORIGINS.has(origin);
}
